import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAxiosInstance from "../../hooks/useAxios";
import { Spinner } from "flowbite-react";
import { MdAdd, MdCancel, MdSearch, MdArrowBack } from "react-icons/md";
import { FaRobot } from "react-icons/fa";

const PAGE_SIZE = 20;

const OrgAgentsPage = () => {
  const { orgId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const axios = useAxiosInstance();
  const orgName = state?.orgName || `Org #${orgId}`;

  // Org-enabled agents — load on mount
  const [orgAgents, setOrgAgents] = useState([]);
  const [orgAgentsLoading, setOrgAgentsLoading] = useState(false);

  // All available agents
  const [allAgents, setAllAgents] = useState([]);
  const [allAgentsTotal, setAllAgentsTotal] = useState(0);
  const [allAgentsPage, setAllAgentsPage] = useState(1);
  const [allAgentsLoading, setAllAgentsLoading] = useState(false);
  const [allAgentsSearch, setAllAgentsSearch] = useState("");

  useEffect(() => {
    fetchOrgAgents();
    fetchAllAgents(1, "");
  }, [orgId]);

  const fetchOrgAgents = () => {
    setOrgAgentsLoading(true);
    axios
      .get(`wkl/custom_agents/custom-agents-sandbox/?org_id=${orgId}`)
      .then((res) => {
        setOrgAgents(res.data.agents || []);
      })
      .catch(() => toast.error("Failed to load org agents"))
      .finally(() => setOrgAgentsLoading(false));
  };

  const fetchAllAgents = (page, search) => {
    setAllAgentsLoading(true);
    const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
    if (search) params.set("search", search);
    axios
      .get(`wkl/custom_agents/custom-agents-sandbox/?${params.toString()}`)
      .then((res) => {
        setAllAgents(res.data.agents || []);
        setAllAgentsTotal(res.data.pagination?.total ?? 0);
      })
      .catch(() => toast.error("Failed to load custom agents"))
      .finally(() => setAllAgentsLoading(false));
  };

  const debounceTimer = useRef(null);

  const handleAllAgentsSearch = (e) => {
    const val = e.target.value;
    setAllAgentsSearch(val);
    setAllAgentsPage(1);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => fetchAllAgents(1, val), 500);
  };

  const handlePageChange = (newPage) => {
    setAllAgentsPage(newPage);
    fetchAllAgents(newPage, allAgentsSearch);
  };

  const enableAgent = (agent) => {
    const prev = orgAgents;
    setOrgAgents((curr) => [...curr, agent]);
    axios
      .post("/accounts/update_org_product/", {
        org_name: orgName,
        report_type: agent.report_type_id,
        subscription_status: ["default"],
        modification_type: "user",
      })
      .then(() => toast.success(`${agent.name} enabled for ${orgName}`))
      .catch(() => {
        toast.error("Failed to enable agent");
        setOrgAgents(prev);
      });
  };

  const disableAgent = (agent) => {
    const prev = orgAgents;
    setOrgAgents((curr) => curr.filter((a) => a.report_type_id !== agent.report_type_id));
    axios
      .post("/accounts/update_org_product/", {
        org_name: orgName,
        report_type: agent.report_type_id,
        subscription_status: ["Hidden"],
        modification_type: "user",
      })
      .then(() => toast.success(`${agent.name} disabled for ${orgName}`))
      .catch(() => {
        toast.error("Failed to disable agent");
        setOrgAgents(prev);
      });
  };

  const enabledIds = new Set((orgAgents || []).map((a) => a.report_type_id));
  const totalPages = Math.ceil(allAgentsTotal / PAGE_SIZE) || 1;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate("/custom-agents")}
          className="text-gray-500 hover:text-black"
        >
          <MdArrowBack size={20} />
        </button>
        <div className="text-2xl font-semibold flex items-center gap-2">
          <FaRobot />
          Custom Agents — {orgName}
        </div>
      </div>

      <div className="flex gap-3 h-[calc(100vh-130px)]">
        {/* Enabled agents */}
        <div className="w-3/4 border bg-white rounded-md flex flex-col overflow-hidden">
          <div className="px-3 py-2 border-b">
            <p className="text-sm font-semibold">Enabled Agents</p>
          </div>
          <div className="overflow-y-auto flex-1 p-2">
            {orgAgentsLoading && (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            )}
            {!orgAgentsLoading && orgAgents.length === 0 && (
              <p className="text-xs text-gray-400 p-2">No agents enabled for this org.</p>
            )}
            <div className="grid grid-cols-2 gap-2">
              {orgAgents.map((agent) => (
                <div
                  key={agent.report_type_id}
                  className="px-3 py-2 bg-slate-50 border border-[#E5E6E4] rounded-md flex justify-between items-start"
                >
                  <div>
                    <p className="text-sm font-medium text-[#343434]">{agent.name}</p>
                    {agent.description && (
                      <p className="text-xs text-gray-500 mt-0.5">{agent.description}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">{agent.creator_name}</p>
                  </div>
                  <button
                    onClick={() => disableAgent(agent)}
                    className="ml-2 mt-0.5 text-gray-400 hover:text-red-500 shrink-0"
                    title="Remove from org"
                  >
                    <MdCancel size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* All available agents */}
        <div className="w-1/4 border bg-white rounded-md flex flex-col overflow-hidden">
          <div className="px-3 py-2 border-b">
            <p className="text-sm font-semibold mb-2">All Agents</p>
            <div className="relative">
              <MdSearch className="absolute left-2 top-2.5 text-gray-400" />
              <input
                className="w-full border rounded px-7 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                placeholder="Search..."
                value={allAgentsSearch}
                onChange={handleAllAgentsSearch}
              />
            </div>
          </div>

          <div className="overflow-y-auto flex-1 p-2">
            {allAgentsLoading && (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            )}
            {!allAgentsLoading &&
              allAgents.map((agent) => {
                const isEnabled = enabledIds.has(agent.report_type_id);
                return (
                  <div key={agent.report_type_id} className="mb-2">
                    <div className="px-2 py-2 bg-slate-50 border border-[#E5E6E4] rounded-md flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-[#343434]">{agent.name}</p>
                        {agent.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{agent.description}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-0.5">{agent.creator_name} · {agent.creator_email}</p>
                        {isEnabled && (
                          <span className="text-xs text-green-600 font-medium">✓ Enabled</span>
                        )}
                      </div>
                      {!isEnabled && (
                        <button
                          onClick={() => enableAgent(agent)}
                          className="ml-2 mt-0.5 text-gray-400 hover:text-green-600 shrink-0"
                          title="Enable for org"
                        >
                          <MdAdd size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            {!allAgentsLoading && allAgents.length === 0 && (
              <p className="text-xs text-gray-400 p-2">No agents found.</p>
            )}
          </div>

          {totalPages > 1 && (
            <div className="border-t p-2 flex items-center justify-between">
              <button
                disabled={allAgentsPage === 1}
                onClick={() => handlePageChange(allAgentsPage - 1)}
                className="px-2 py-1 text-xs border rounded disabled:opacity-40 hover:bg-slate-50"
              >
                Prev
              </button>
              <span className="text-xs text-gray-500">
                {allAgentsPage} / {totalPages}
              </span>
              <button
                disabled={allAgentsPage >= totalPages}
                onClick={() => handlePageChange(allAgentsPage + 1)}
                className="px-2 py-1 text-xs border rounded disabled:opacity-40 hover:bg-slate-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrgAgentsPage;
